import { addDays, format } from 'date-fns';
import { it } from 'date-fns/locale';
import { ChevronLeft, ChevronRight, Users, X } from 'lucide-react';
import { NotionSelect } from '../../ui/notion/NotionSelect';
import type { Department } from '../../../lib/stores/calendarSharingStore';
import type { CalendarPerson } from '../../../lib/calendar/sharing';
import { PersonName } from '../../shared/PersonName';

const btn = 'rounded px-1.5 py-0.5 text-[13px] text-[#787774] hover:bg-[rgba(55,53,47,0.08)] hover:text-[#37352F] dark:text-white/70 dark:hover:bg-white/10';
const field = 'rounded bg-transparent px-1.5 py-0.5 text-[13px] text-[#37352F] hover:bg-[rgba(55,53,47,0.08)] focus:bg-[rgba(55,53,47,0.08)] focus:outline-none dark:text-white dark:hover:bg-white/10';
const DURATIONS = [15, 30, 60, 90];

interface Props {
  day: Date;
  onDay: (d: Date) => void;
  departments: Department[];
  /** persone che vedo, per reparto */
  seen: CalendarPerson[];
  departmentId: string;
  onDepartment: (id: string) => void;
  inDepartment: CalendarPerson[];
  picked: string[] | null;
  onPicked: (ids: string[] | null) => void;
  freeAt: string;
  onFreeAt: (v: string) => void;
  duration: number;
  onDuration: (m: number) => void;
  compact?: boolean;
}

export function TeamToolbar(p: Props) {
  const shownCount = p.picked ? p.inDepartment.filter(x => p.picked!.includes(x.id)).length : p.inDepartment.length;
  const toggle = (id: string) => {
    const cur = p.picked ?? p.inDepartment.map(x => x.id);
    const next = cur.includes(id) ? cur.filter(x => x !== id) : [...cur, id];
    p.onPicked(next.length === p.inDepartment.length ? null : next);
  };
  const deptCount = (id: string) => p.seen.filter(x => id === 'all' || x.departmentIds.includes(id)).length;

  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-2 border-b border-[rgba(55,53,47,0.09)] px-2 py-2 dark:border-white/10">
      <div className="flex items-center gap-0.5">
        <button type="button" className={btn} onClick={() => p.onDay(new Date())}>Oggi</button>
        <button type="button" className={btn} aria-label="Giorno precedente" onClick={() => p.onDay(addDays(p.day, -1))}><ChevronLeft className="h-4 w-4" /></button>
        <button type="button" className={btn} aria-label="Giorno successivo" onClick={() => p.onDay(addDays(p.day, 1))}><ChevronRight className="h-4 w-4" /></button>
        <input
          type="date"
          aria-label="Data"
          className={field}
          value={format(p.day, 'yyyy-MM-dd')}
          onChange={e => { const d = new Date(`${e.target.value}T00:00:00`); if (!Number.isNaN(d.getTime())) p.onDay(d); }}
        />
        {!p.compact && <span className="ml-2 text-sm font-medium capitalize text-[#37352F] dark:text-white">{format(p.day, 'EEEE d MMMM', { locale: it })}</span>}
      </div>

      <NotionSelect
        variant="flat"
        aria-label="Reparto"
        value={p.departmentId}
        onChange={e => { p.onDepartment(e.target.value); p.onPicked(null); }}
        options={[
          { value: 'all', label: `Tutti i reparti · ${deptCount('all')} di ${p.seen.length}` },
          ...p.departments.map(d => ({ value: d.id, label: `${d.name} · ${deptCount(d.id)} di ${p.seen.length}` })),
        ]}
      />

      <details className="relative">
        <summary className={`${field} flex cursor-pointer list-none items-center gap-1`}><Users className="h-3.5 w-3.5 text-[#787774]" strokeWidth={1.5} />Persone · {shownCount}</summary>
        <div className="absolute left-0 top-full z-20 mt-1 max-h-64 w-56 overflow-y-auto rounded-md border border-[rgba(55,53,47,0.09)] bg-white p-1 shadow-md dark:border-white/10 dark:bg-[#252525]">
          {p.inDepartment.map(x => (
            <label key={x.id} className="flex cursor-pointer items-center gap-2 rounded px-2 py-1 text-[13px] hover:bg-[rgba(55,53,47,0.08)] dark:hover:bg-white/10">
              <input type="checkbox" checked={!p.picked || p.picked.includes(x.id)} onChange={() => toggle(x.id)} />
              <PersonName name={x.name} />
            </label>
          ))}
          {p.picked && <button type="button" className={`${btn} w-full text-left`} onClick={() => p.onPicked(null)}>Mostra tutti</button>}
        </div>
      </details>

      <div className="flex items-center gap-1 text-[13px] text-[#787774]">
        <span>Liberi alle</span>
        <input type="time" step={900} className={field} value={p.freeAt} onChange={e => p.onFreeAt(e.target.value)} aria-label="Liberi alle" />
        <span>per</span>
        <NotionSelect variant="flat" value={String(p.duration)} onChange={e => p.onDuration(Number(e.target.value))} aria-label="Durata"
          options={DURATIONS.map(m => ({ value: String(m), label: `${m} min` }))} />
        {p.freeAt && <button type="button" className={btn} aria-label="Togli il filtro" onClick={() => p.onFreeAt('')}><X className="h-3.5 w-3.5" strokeWidth={1.5} /></button>}
      </div>
    </div>
  );
}
